import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, Copy } from 'lucide-react';
import { SaButton, sa, errMsg } from './ui';
import { getSite } from '../../lib/site';

export const SuperAdminAccounts = () => {
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState(null);
  const load = useCallback(() => axios.get('/superadmin/admins').then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const createInvite = async () => {
    try {
      const { data: d } = await axios.post('/superadmin/admins/invite');
      const url = getSite().platform?.preview ? `${window.location.origin}${d.setup_path}&__site=admin` : d.setup_url;
      setInvite({ ...d, setup_url: url }); load(); toast.success('Setup link created — valid for 1 hour');
    }
    catch (e) { toast.error(errMsg(e, 'Could not create link')); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(invite.setup_url); toast.success('Copied'); } catch (e) { toast.error('Copy failed — select the link manually'); } };
  const remove = async (u) => {
    if (!window.confirm(`Remove super admin ${u.email}? They will lose platform access immediately.`)) return;
    try { await axios.delete(`/superadmin/admins/${u.id}`); toast.success('Removed'); load(); } catch (e) { toast.error(errMsg(e, 'Could not remove')); }
  };

  if (!data) return null;
  return (
    <div data-testid="sa-accounts" style={{ ...sa.surface, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ ...sa.overline, margin: 0 }}>Platform access</p>
          <h3 style={{ ...sa.heading, fontSize: 20, margin: '4px 0 0' }}>Super Admin accounts</h3>
        </div>
        <SaButton data-testid="sa-accounts-invite" onClick={createInvite}><UserPlus size={14} /> New super admin link</SaButton>
      </div>
      <p style={{ color: '#A1A1AA', fontSize: 13, lineHeight: 1.5 }}>Each super admin chooses their own email and password through a single-use setup link (expires in 1 hour). Passwords are never shown or sent by chat. Remove accounts you no longer use.</p>
      {invite && (
        <div data-testid="sa-accounts-invite-link" style={{ background: '#0A0A0A', border: '1px solid #3F3F46', padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ fontSize: 12, color: '#fbbf24', wordBreak: 'break-all', flex: 1 }}>{invite.setup_url}</code>
          <SaButton variant="ghost" data-testid="sa-accounts-copy" onClick={copy}><Copy size={12} /> Copy</SaButton>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 13 }}>
        <tbody>
          {data.items.map((u) => (
            <tr key={u.id} data-testid={`sa-account-${u.id}`} style={{ borderTop: '1px solid #27272A' }}>
              <td style={{ padding: '10px 0' }}><b>{u.email}</b>{u.id === data.me && <span style={{ color: '#22c55e', marginLeft: 8, fontSize: 11 }}>YOU</span>}<div style={{ color: '#A1A1AA', fontSize: 12 }}>{u.name}{u.created_by ? ` · added by ${u.created_by}` : ''}</div></td>
              <td style={{ textAlign: 'right' }}>{u.id !== data.me && <SaButton variant="ghost" data-testid={`sa-account-remove-${u.id}`} onClick={() => remove(u)}><Trash2 size={12} /> Remove</SaButton>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.pending_invites > 0 && <p style={{ color: '#fbbf24', fontSize: 12, marginTop: 10 }}>{data.pending_invites} unused setup link(s) outstanding.</p>}
    </div>
  );
};
